import { PLATFORM_INSTITUTION_CODE, ROLE_LABELS } from "@eduleo/shared";
import { ChevronDown, KeyRound, LogOut, Settings, ShieldCheck } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { cn } from "../../lib/cn";
import { useAuthStore } from "../../stores/auth-store";

export function initials(name: string): string {
  const parts = name
    .replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, "")
    .trim()
    .split(/\s+/);
  return `${parts[0]?.[0] ?? ""}${parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : ""}`.toUpperCase();
}

export function roleLabel(roles: readonly string[]): string {
  const first = roles[0] as keyof typeof ROLE_LABELS | undefined;
  // Someone with only their school's own roles has no built-in one.
  return first ? ROLE_LABELS[first] : "Staff member";
}

export function UserMenu({
  onSignOut,
  signingOut,
}: {
  onSignOut: () => void;
  signingOut: boolean;
}) {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const role = roleLabel(user.roles);
  // Eduleo's team protects its sign-in with two-step verification.
  const platform = user.institution.code === PLATFORM_INSTITUTION_CODE;

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account menu for ${user.displayName}`}
        className="flex h-11 cursor-pointer items-center gap-2.5 rounded-full py-1 pr-2 pl-1 hover:bg-surface-2 md:pr-3"
      >
        <span className="grid size-9 place-items-center rounded-full bg-brand-soft font-bold text-brand-fg text-sm">
          {initials(user.displayName)}
        </span>
        <span className="hidden min-w-0 flex-col text-left leading-tight md:flex">
          <span className="truncate font-semibold text-sm">{user.displayName}</span>
          <span className="truncate text-ink-3 text-xs">{role}</span>
        </span>
        <ChevronDown aria-hidden className="hidden size-4 text-ink-3 md:block" strokeWidth={1.5} />
      </button>

      <div
        id={menuId}
        hidden={!open}
        className={cn(
          "absolute right-0 z-40 mt-2 w-64 rounded-md border border-edge bg-surface p-2 shadow-lg",
        )}
      >
        <div className="px-3 py-2">
          <p className="truncate font-semibold">{user.displayName}</p>
          <p className="truncate text-ink-3 text-sm">
            {role} · {user.institution.name}
          </p>
          <p className="mt-1 font-mono text-ink-3 text-xs">
            {user.institution.code} / {user.loginId}
          </p>
        </div>
        <hr className="my-1 border-edge" />
        {platform && (
          <Link
            to="/platform/settings"
            onClick={() => setOpen(false)}
            className="flex h-11 w-full items-center gap-2 rounded-md px-3 text-left hover:bg-surface-2 md:h-9"
          >
            <Settings aria-hidden className="size-4" strokeWidth={1.5} />
            Profile and settings
          </Link>
        )}
        <Link
          to="/change-password"
          state={{ from: `${location.pathname}${location.search}` }}
          onClick={() => setOpen(false)}
          className="flex h-11 w-full items-center gap-2 rounded-md px-3 text-left hover:bg-surface-2 md:h-9"
        >
          <KeyRound aria-hidden className="size-4" strokeWidth={1.5} />
          Change password
        </Link>
        {platform && (
          <Link
            to="/platform/settings/security"
            onClick={() => setOpen(false)}
            className="flex h-11 w-full items-center gap-2 rounded-md px-3 text-left hover:bg-surface-2 md:h-9"
          >
            <ShieldCheck aria-hidden className="size-4" strokeWidth={1.5} />
            Sign-in security
          </Link>
        )}
        <button
          type="button"
          onClick={onSignOut}
          disabled={signingOut}
          className="flex h-11 w-full cursor-pointer items-center gap-2 rounded-md px-3 text-left text-danger-fg hover:bg-danger-soft disabled:opacity-50 md:h-9"
        >
          <LogOut aria-hidden className="size-4" strokeWidth={1.5} />
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </div>
  );
}
